import React, { useState, useRef, useCallback, useMemo } from "react";
import { useClickOutside } from "../../../hooks/useClickOutside";

/**
 * District represents a Polish powiat (county)
 */
export interface District {
  code: string;
  name: string;
  slug: string;
  voivodeship: string;
}

/**
 * Props for SubregionsMultiSelect component
 */
export interface SubregionsMultiSelectProps {
  /** List of available districts (powiaty) to select from */
  districts: District[];
  /** Currently selected district TERYT codes */
  value: string[];
  /** Callback invoked when selected districts change */
  onChange: (value: string[]) => void;
  /** Whether district data is currently loading */
  isLoading?: boolean;
  /** Whether an error occurred while loading districts */
  isError?: boolean;
  /** Callback to retry loading districts after an error */
  onRetry?: () => void;
}

export const SubregionsMultiSelect: React.FC<SubregionsMultiSelectProps> = ({
  districts,
  value,
  onChange,
  isLoading = false,
  isError = false,
  onRetry,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
  }, []);

  useClickOutside(dropdownRef, handleClickOutside, isOpen);

  const filteredDistricts = useMemo(
    () =>
      districts.filter((district) =>
        district.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [districts, searchQuery]
  );

  const toggleDistrict = (code: string) => {
    if (value.includes(code)) {
      onChange(value.filter((item) => item !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const getDistrictName = (code: string): string => {
    const district = districts.find((d) => d.code === code);
    return district?.name || code;
  };

  if (isError) {
    return (
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
        <p className="text-sm text-amber-700">Nie udało się załadować powiatów</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 text-sm text-primary-600 hover:text-primary-800 font-medium"
          >
            Spróbuj ponownie
          </button>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded-md bg-neutral-50">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
        <span className="text-sm text-neutral-500">Ładowanie powiatów...</span>
      </div>
    );
  }

  if (districts.length === 0) {
    return (
      <div className="px-3 py-2 border border-neutral-200 rounded-md bg-neutral-50">
        <span className="text-sm text-neutral-500">Brak powiatów dla wybranych województw</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-left flex items-center justify-between"
      >
        <span className={value.length > 0 ? "text-neutral-900" : "text-neutral-500"}>
          {value.length > 0
            ? `Wybrane powiaty: ${value.length}`
            : "Wybierz powiaty (opcjonalnie)"}
        </span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg z-[60] max-h-80 overflow-hidden flex flex-col">
          <div className="sticky top-0 bg-white border-b border-neutral-200 p-3">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Szukaj powiatu..."
                className="w-full pl-9 pr-8 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            {value.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="mt-2 text-xs text-primary-600 hover:text-primary-800"
              >
                Wyczyść wszystkie
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {filteredDistricts.length === 0 ? (
              <div className="text-center text-neutral-500 py-4 text-sm">
                Brak pasujących powiatów
              </div>
            ) : (
              filteredDistricts.map((district) => {
                const isSelected = value.includes(district.code);
                return (
                  <label
                    key={district.code}
                    className={`flex items-center gap-2 px-4 py-2 text-sm cursor-pointer hover:bg-neutral-50 ${
                      isSelected ? "bg-primary-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleDistrict(district.code)}
                      className="h-4 w-4 text-primary-600 border-neutral-300 rounded"
                    />
                    <span className="text-sm text-neutral-900">{district.name}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}

      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto">
          {value.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 text-neutral-700 text-xs rounded-full"
            >
              {getDistrictName(code)}
              <button
                type="button"
                onClick={() => toggleDistrict(code)}
                className="text-neutral-500 hover:text-neutral-700"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
